/**
 * Shared checkout gate for cart blocks that must be completed before a
 * customer can check out (installation address, non-potable water
 * acknowledgement, ...). See snippets/cart-checkout-gate.liquid for the
 * markup contract.
 *
 * Loaded as an external module from snippets/cart-products.liquid, above
 * the empty-cart branch, so it runs on every page that renders a cart -
 * including a page whose cart starts EMPTY. The cart drawer morphs its
 * contents in when the first item is added, and morphed HTML does not
 * execute inline scripts (assets/morph.js), so a script shipped inside the
 * block itself would never run and checkout would go ungated.
 */
(function () {
  // The cart section is morphed in place on quantity changes, which does
  // not re-run this script - so all listeners are delegated from the
  // document and installed only once, and the gated blocks are read from
  // the DOM at call time rather than captured here.
  if (window.cartCheckoutGate) {
    window.cartCheckoutGate.sync();
    return;
  }

  var BLOCK = '[data-checkout-gate]';
  var REQUIRED = '[data-checkout-gate-required]';
  var ERROR = '[data-checkout-gate-error]';
  var PATTERN_ATTR = 'data-checkout-gate-pattern';
  var MESSAGE_ATTR = 'data-checkout-gate-message';
  var INVALID_CLASS = 'checkout-gate-invalid';

  function blocks() {
    return Array.prototype.slice.call(document.querySelectorAll(BLOCK));
  }

  // A field the customer cannot see cannot be required of them - blocks
  // hide the fields that do not apply (a country with no provinces, say).
  function isVisible(el) {
    return !el.closest('[hidden]') && el.offsetParent !== null;
  }

  function requiredFields(block) {
    return Array.prototype.slice.call(block.querySelectorAll(REQUIRED)).filter(isVisible);
  }

  function isComplete(input) {
    return input.type === 'checkbox' ? input.checked : input.value.trim().length > 0;
  }

  // A filled field must also match its pattern, if it declares one. The
  // whole value has to match, so a pattern cannot pass on a substring.
  // Unicode mode is tried first so patterns can use \p{L} and accept
  // non-Latin place names; 'i' alone is the fallback for patterns that
  // are only valid outside unicode mode.
  function isWellFormed(input) {
    var pattern = input.getAttribute(PATTERN_ATTR);
    if (!pattern) return true;

    var source = '^(?:' + pattern + ')$';
    var value = input.value.trim();

    try {
      return new RegExp(source, 'iu').test(value);
    } catch (e) {
      try {
        return new RegExp(source, 'i').test(value);
      } catch (e2) {
        // A malformed pattern is an author error - never strand a customer.
        return true;
      }
    }
  }

  function isValid(input) {
    return isComplete(input) && isWellFormed(input);
  }

  function blockValid(block) {
    return requiredFields(block).every(isValid);
  }

  // No gated block on the page means nothing to hold checkout for.
  function allValid() {
    return blocks().every(blockValid);
  }

  // The block's own error text covers "you have not filled this in";
  // fields add their own message when what they contain is malformed.
  function errorText(block) {
    var error = block.querySelector(ERROR);
    if (!error) return '';

    if (error.dataset.defaultMessage == null) {
      error.dataset.defaultMessage = error.textContent.trim();
    }

    var fields = requiredFields(block);
    var parts = [];

    if (fields.some(function (input) { return !isComplete(input); })) {
      parts.push(error.dataset.defaultMessage);
    }

    fields.forEach(function (input) {
      if (!isComplete(input) || isWellFormed(input)) return;
      var message = input.getAttribute(MESSAGE_ATTR);
      if (message && parts.indexOf(message) === -1) parts.push(message);
    });

    return parts.join(' ');
  }

  // Called when a checkout attempt is blocked: flag every incomplete
  // block, and scroll to the first one only.
  function reportInvalid() {
    var first = null;

    blocks().forEach(function (block) {
      var valid = blockValid(block);

      requiredFields(block).forEach(function (input) {
        input.classList.toggle(INVALID_CLASS, !isValid(input));
      });

      var error = block.querySelector(ERROR);
      if (error) {
        if (!valid) error.textContent = errorText(block);
        error.hidden = valid;
      }

      if (!valid && !first) first = block;
    });

    if (first) first.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  // Release what the customer has since corrected, without flagging
  // anything they simply have not reached yet.
  function clearResolved() {
    blocks().forEach(function (block) {
      requiredFields(block).forEach(function (input) {
        if (isValid(input)) input.classList.remove(INVALID_CLASS);
      });

      var error = block.querySelector(ERROR);
      if (!error) return;

      if (blockValid(block)) {
        error.hidden = true;
      } else if (!error.hidden) {
        // Keep a visible error in step as the customer edits.
        error.textContent = errorText(block);
      }
    });
  }

  function sync() {
    var blocked = !allValid();

    document.querySelectorAll('.additional-checkout-buttons').forEach(function (group) {
      group.classList.toggle('cart-checkout-blocked', blocked);
      if (blocked) {
        group.setAttribute('aria-disabled', 'true');
      } else {
        group.removeAttribute('aria-disabled');
      }
    });
  }

  // Block the main Checkout button.
  document.addEventListener(
    'submit',
    function (e) {
      var form = e.target;
      if (!form || form.id !== 'cart-form') return;

      var submitter = e.submitter;
      var isCheckout = (submitter && submitter.name === 'checkout') || !submitter;
      if (isCheckout && !allValid()) {
        e.preventDefault();
        reportInvalid();
      }
    },
    true
  );

  // Block the accelerated checkout buttons. Their children are made inert
  // via CSS (this also covers cross-origin iframes), so the click lands on
  // the wrapper and only surfaces the error.
  document.addEventListener(
    'click',
    function (e) {
      var group = e.target.closest && e.target.closest('.additional-checkout-buttons');
      if (!group || allValid()) return;
      e.preventDefault();
      e.stopPropagation();
      reportInvalid();
    },
    true
  );

  // Keep the buttons and error states in step as the customer fills the
  // blocks in.
  function onEdit(e) {
    if (!e.target.closest || !e.target.closest(BLOCK)) return;
    clearResolved();
    sync();
  }
  document.addEventListener('input', onEdit);
  document.addEventListener('change', onEdit);

  // Re-apply state after the cart section re-renders. The event fires just
  // before the section is morphed, so re-sync on the next frame.
  document.addEventListener('cart:update', function () {
    requestAnimationFrame(sync);
  });

  window.cartCheckoutGate = { sync: sync };

  // The summary block (and its accelerated buttons) renders after this
  // script, so the first sync has to wait for the rest of the page.
  sync();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sync);
  }

  // Backstop for any path that swaps the cart in without firing cart:update
  // (the drawer rendering its first item, for one). Blocking itself never
  // depends on this - allValid() reads the DOM at event time - but the
  // accelerated buttons' dimmed state would otherwise go stale.
  if (typeof MutationObserver === 'function') {
    var pending = false;
    new MutationObserver(function () {
      if (pending) return;
      pending = true;
      requestAnimationFrame(function () {
        pending = false;
        sync();
      });
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
})();
